import * as Sentry from '@sentry/vue'
import { usePersonalStore } from '~/stores/personal'
import getCurrentHost from '~/utils/api-utils'
import {
  REGISTRATION_CONSENT_PROCESS,
  SESSION_KEY_OAUTH_STATE,
  SESSION_KEY_PKCE_VERIFIER,
  SESSION_KEY_REG_DATA,
} from '~/utils/prebooking-constants'

interface ConsentItem {
  slug: string
  required?: boolean
  granted?: boolean
}

async function generatePKCE(): Promise<{ verifier: string, challenge: string }> {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  const verifier = btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')

  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  const challenge = btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')

  return { verifier, challenge }
}

function generateOAuthState(): string {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

/**
 * Fetches the consent slugs required for registration for the given legalEntityId
 * from the consents service — same pattern as customer-registration-ui.
 * All consents are sent as granted: false (opt-in, not mandatory for registration).
 */
async function fetchConsentsForLegalEntity(legalEntityId: number): Promise<ConsentItem[]> {
  try {
    const consents = await $fetch<ConsentItem[]>(
      `api/customer/consents/v1/legal-entities/${legalEntityId}/processes/${REGISTRATION_CONSENT_PROCESS}/consents`,
      {
        method: 'GET',
        baseURL: getCurrentHost(),
      },
    )
    return (consents ?? []).map((c: ConsentItem): ConsentItem => ({ slug: c.slug, granted: false }))
  }
  catch (err: unknown) {
    Sentry.captureException(err, {
      level: 'warning',
      tags: { flow: 'new-user', step: 'fetch-consents' },
      extra: { legalEntityId },
    })
    // Fallback to empty — CuDa will still create the customer, just without consents
    return []
  }
}

/**
 * Composable for the pre-booking OIDC registration flow (CCT-2060).
 *
 * Stores registration data in sessionStorage, generates PKCE, and redirects
 * to Keycloak's prebooking-client auth URL. Keycloak's register-prebooking.ftl
 * reads the sessionStorage data and auto-submits it (invisible to the user).
 *
 * After Keycloak redirects back with an auth code, the /personal/auth-callback
 * route sends the code + PKCE verifier to the Nuxt server route
 * (/api/auth/token), which exchanges them with Keycloak server-to-server and
 * sets an HttpOnly cookie — the raw JWT never touches the browser.
 */
export function usePrebookingOidcRegistration() {
  // Returns the Keycloak base URL and the redirect_uri origin to use.
  // In prod both are the same origin as the app (www.free2move.com).
  // In dev we use www.int.free2move.com for both — same origin means sessionStorage
  // is shared between the Keycloak page and the prebooking app served there.
  function getKeycloakConfig(): { keycloakBase: string, redirectOrigin: string } {
    if (typeof window === 'undefined')
      return { keycloakBase: '', redirectOrigin: '' }
    if (window.location.hostname === 'localhost') {
      return {
        keycloakBase: 'https://www.int.free2move.com',
        redirectOrigin: 'https://www.int.free2move.com',
      }
    }
    return {
      keycloakBase: window.location.origin,
      redirectOrigin: window.location.origin,
    }
  }

  async function redirectToKeycloakRegistration(
    personalData: {
      email: string
      mobilePhone: string
      firstName?: string
      lastName?: string
      country?: string
      addressStreet?: string
      addressCity?: string
      addressZip?: string
      password?: string
      legalEntityId: number
    },
    locale: { lang: string, city: string },
  ) {
    const [{ verifier, challenge }, consents, state] = await Promise.all([
      generatePKCE(),
      fetchConsentsForLegalEntity(personalData.legalEntityId),
      generateOAuthState(),
    ])

    const mobilePhone = personalData.mobilePhone ?? ''

    const regData = {
      email: personalData.email,
      mobilePhone,
      firstName: personalData.firstName ?? '',
      lastName: personalData.lastName ?? '',
      country: personalData.country ?? '',
      street: personalData.addressStreet ?? '',
      city: personalData.addressCity ?? '',
      zip: personalData.addressZip ?? '',
      password: personalData.password ?? '',
      legalEntityId: String(personalData.legalEntityId),
      // Store consents as array — JSON.stringify(regData) encodes it once.
      // The FTL detects typeof !== 'string' and re-stringifies for the hidden input.
      consents,
    }

    let regDataJson: string
    try {
      regDataJson = JSON.stringify(regData)
    }
    catch (e) {
      console.error('[prebooking] Failed to serialize regData:', e)
      Sentry.captureException(e, {
        level: 'error',
        tags: { flow: 'new-user', step: 'serialize-reg-data' },
      })
      return
    }

    sessionStorage.setItem(SESSION_KEY_PKCE_VERIFIER, verifier)
    sessionStorage.setItem(SESSION_KEY_REG_DATA, regDataJson)
    sessionStorage.setItem(SESSION_KEY_OAUTH_STATE, state)

    // Clear the password from Pinia store now that it's been written to sessionStorage.
    if (typeof window !== 'undefined') {
      usePersonalStore().password = undefined

      // Persist all store state before redirecting to Keycloak
      // This ensures sidebar and other UI state is preserved after returning from redirect
      const { $persistPiniaState } = useNuxtApp()
      $persistPiniaState()
    }

    // Redirect through internal endpoint to clear Keycloak cookies server-side
    const params = new URLSearchParams({
      lang: locale.lang,
      city: locale.city,
      challenge,
      state,
    })

    window.location.href = `/rental/booking/api/auth/registration-redirect?${params.toString()}`
  }

  async function exchangeCodeForToken(code: string, lang: string, city: string): Promise<boolean> {
    const verifier = sessionStorage.getItem(SESSION_KEY_PKCE_VERIFIER)
    if (!verifier) {
      Sentry.captureMessage('[auth-callback] PKCE verifier missing in sessionStorage during token exchange', {
        level: 'error',
        tags: { flow: 'new-user', step: 'token-exchange' },
      })
      return false
    }

    const { redirectOrigin } = getKeycloakConfig()
    const redirectUri = `${redirectOrigin}/rental/booking/${lang}/${city}/personal/auth-callback`

    try {
      // POST to the Nuxt server route which exchanges the code with Keycloak
      // server-to-server and sets an HttpOnly cookie — the raw JWT never reaches
      // the browser.
      await $fetch('/api/auth/token', {
        method: 'POST',
        body: {
          code,
          codeVerifier: verifier,
          redirectUri,
        },
      })

      sessionStorage.removeItem(SESSION_KEY_PKCE_VERIFIER)
      sessionStorage.removeItem(SESSION_KEY_REG_DATA)
      return true
    }
    catch (err: unknown) {
      Sentry.captureException(err, {
        level: 'error',
        tags: { flow: 'new-user', step: 'token-exchange' },
      })
      sessionStorage.removeItem(SESSION_KEY_REG_DATA)
      return false
    }
  }

  return {
    redirectToKeycloakRegistration,
    exchangeCodeForToken,
  }
}
